/*
 * OPC (Option Price Calculator) — mobile photo upload fix
 *
 * Problem this solves
 * -------------------
 * The app's "Upload Your Design" field renders its file input with an
 * extension-only accept list (".AI,.EPS,.SVG,.PDF,.PSD,.PNG,.JPG,.TIFF") and
 * validates the chosen file by extension. That breaks phone uploads twice over:
 *
 *   1. iOS matches Photo Library assets by UTI, not by filename extension, so an
 *      extension-only accept list leaves every photo greyed out and unselectable.
 *   2. iPhones shoot HEIC, and when Safari transcodes a photo for upload it names
 *      the result ".jpeg" — which the app's validator rejects, because its list
 *      has ".JPG" but not ".JPEG". The rejection is silent: the file count stays
 *      at 0 and no error is shown.
 *
 * What this does
 * --------------
 *   1. Adds real MIME types to `accept` so iOS offers a selectable Photo Library.
 *      image/heic is deliberately NOT included — leaving it out is what makes iOS
 *      transcode HEIC to JPEG at pick time.
 *   2. Intercepts the change event before the app's own handler and renames or
 *      converts the file to something the validator accepts (.jpeg -> .jpg,
 *      .tif -> .tiff, HEIC/HEIF -> re-encoded JPEG).
 *
 * This is a client-side shim over a third-party app. The durable fix is to widen
 * the allowed file types in the OPC app admin; if that ever happens this file can
 * be removed. If the app changes its markup, the selector below is what to check.
 */
(function () {
  'use strict';

  var INPUT_SELECTOR = 'input.multifileupload-input, input.option-multifileupload-input';

  // Extensions the app's validator accepts. Verified against the live widget.
  var ALLOWED = ['ai', 'eps', 'svg', 'pdf', 'psd', 'png', 'jpg', 'tiff'];

  // Added to `accept` so iOS presents photos as selectable.
  var MIME = ['image/jpeg', 'image/png', 'image/tiff', 'image/svg+xml', 'application/pdf'];

  // Same bytes, wrong extension — a rename is enough.
  var RENAME = { jpeg: 'jpg', jfif: 'jpg', tif: 'tiff' };

  // Needs decoding and re-encoding to become an accepted format.
  var CONVERT = ['heic', 'heif'];

  // iOS Safari fails to rasterise canvases much beyond this area.
  var MAX_CANVAS_AREA = 16000000;

  var REENTRY_FLAG = '__opcUploadFixReentry';

  function extensionOf(name) {
    var dot = String(name).lastIndexOf('.');
    return dot < 0 ? '' : String(name).slice(dot + 1).toLowerCase();
  }

  function stemOf(name) {
    var dot = String(name).lastIndexOf('.');
    return dot < 0 ? String(name) : String(name).slice(0, dot);
  }

  function needsIntercept(file) {
    var ext = extensionOf(file.name);
    if (Object.prototype.hasOwnProperty.call(RENAME, ext)) return true;
    if (CONVERT.indexOf(ext) !== -1) return true;
    // Nothing we can convert, but the app would drop it without a word, so we
    // still take the event in order to explain what happened.
    return ALLOWED.indexOf(ext) === -1;
  }

  function patchAccept(input) {
    var current = (input.getAttribute('accept') || '')
      .split(',')
      .map(function (part) { return part.trim(); })
      .filter(Boolean);

    var missing = MIME.filter(function (type) { return current.indexOf(type) === -1; });
    if (!missing.length) return;

    input.setAttribute('accept', current.concat(missing).join(','));
  }

  function decode(file) {
    if (typeof createImageBitmap !== 'function') return Promise.reject(new Error('no createImageBitmap'));
    return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () {
      // Older Safari rejects the options bag rather than ignoring it.
      return createImageBitmap(file);
    });
  }

  function toJpeg(file) {
    return decode(file).then(function (bitmap) {
      var width = bitmap.width;
      var height = bitmap.height;
      var area = width * height;

      if (area > MAX_CANVAS_AREA) {
        var scale = Math.sqrt(MAX_CANVAS_AREA / area);
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));
      }

      var canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
      if (typeof bitmap.close === 'function') bitmap.close();

      return new Promise(function (resolve, reject) {
        canvas.toBlob(function (blob) {
          if (!blob) return reject(new Error('canvas encode failed'));
          resolve(new File([blob], stemOf(file.name) + '.jpg', { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.92);
      });
    });
  }

  function normalize(file) {
    var ext = extensionOf(file.name);

    if (Object.prototype.hasOwnProperty.call(RENAME, ext)) {
      var renamed = stemOf(file.name) + '.' + RENAME[ext];
      return Promise.resolve(new File([file], renamed, { type: file.type, lastModified: file.lastModified }));
    }

    if (CONVERT.indexOf(ext) !== -1) {
      return toJpeg(file).catch(function (error) {
        // Hand the original back untouched rather than mislabel bytes the studio
        // can't open. showDropNotice explains the drop, since the app won't.
        console.warn('[opc-upload-fix] could not convert ' + file.name, error);
        return file;
      });
    }

    return Promise.resolve(file);
  }

  // The app drops unacceptable files without saying anything, so say it for it.
  function showDropNotice(input, names) {
    var holder = input.closest('.fileupload') || input.parentElement;
    if (!holder) return;

    var notice = holder.querySelector('.opc-upload-fix-notice');
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'opc-upload-fix-notice';
      notice.setAttribute('role', 'status');
      notice.style.cssText = 'color:#c62828;font-size:12px;margin:6px 0;';
      holder.appendChild(notice);
    }

    notice.textContent =
      names.join(', ') +
      " couldn't be added. Please upload a JPG, PNG, PDF, SVG, AI, EPS, PSD or TIFF file.";
  }

  function clearDropNotice(input) {
    var holder = input.closest('.fileupload') || input.parentElement;
    var notice = holder && holder.querySelector('.opc-upload-fix-notice');
    if (notice) notice.remove();
  }

  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || typeof input.matches !== 'function' || !input.matches(INPUT_SELECTOR)) return;

      // Our own re-dispatch — let it through to the app's handler.
      if (input[REENTRY_FLAG]) {
        input[REENTRY_FLAG] = false;
        return;
      }

      var files = Array.prototype.slice.call(input.files || []);
      if (!files.length || !files.some(needsIntercept)) return;

      // Hold the event until the replacement files are ready.
      event.stopImmediatePropagation();
      event.preventDefault();

      Promise.all(files.map(normalize)).then(function (normalized) {
        var stillBad = normalized.filter(function (file) {
          return ALLOWED.indexOf(extensionOf(file.name)) === -1;
        });

        var transfer = new DataTransfer();
        normalized.forEach(function (file) { transfer.items.add(file); });

        input[REENTRY_FLAG] = true;
        input.files = transfer.files;

        if (stillBad.length) {
          showDropNotice(input, stillBad.map(function (file) { return file.name; }));
        } else {
          clearDropNotice(input);
        }

        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    },
    true
  );

  function scan(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    Array.prototype.forEach.call(root.querySelectorAll(INPUT_SELECTOR), patchAccept);
  }

  // The app injects and re-renders its options long after page load.
  function watch() {
    var root = document.documentElement || document.body;
    if (!root) {
      // Script ran before the document element existed.
      document.addEventListener('DOMContentLoaded', watch, { once: true });
      return;
    }

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          patchAccept(mutation.target);
          return;
        }
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          if (typeof node.matches === 'function' && node.matches(INPUT_SELECTOR)) patchAccept(node);
          scan(node);
        });
      });
    }).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['accept'],
    });

    scan(document);
  }

  watch();
})();
